import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import { RiskFactorsModalComponent } from '../modals/risk-factors-modal/risk-factors-modal.component';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { TestFormulaModalComponent } from '../modals/test-formula-modal/test-formula-modal.component';
import { RiskFactorModalComponent } from '../modals/risk-factor-modal/risk-factor-modal.component';
import { AutoComplete } from 'primeng/autocomplete';
import { RiskEquationPart, RiskFormula } from '../../../models/risk-formula';
import { AgilityRiskFormulaHelperService } from '../../../providers/agility-risk-formula-helper.service';
import { AgilityRiskService } from '../../../providers/agility-risk.service';
import { EquationPartTypes } from '../../../models/risk-equation-part-type';
import { RiskFactor } from '../../../models/risk-factor';
import { RiskFormulaType } from '../../../models/risk-formula-type';
import { RiskBand } from '../../../models/risk-band';
import { Subscription } from 'rxjs';
import { AgilityRiskSpinnerService } from '../../../providers/agility-risk-spinner.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'lib-advanced-risk-formula-management',
  templateUrl: './advanced-risk-formula-management.component.html',
  styleUrls: ['./advanced-risk-formula-management.component.scss']
})
export class AdvancedRiskFormulaManagementComponent {

  @ViewChildren('input', {read: AutoComplete}) inputs: QueryList<AutoComplete>;

  @Input() set formula(value: RiskFormula) {
    if (value) {
      this.initComponent(value);
    }
  }

  formulaUI: RiskFormula;
  riskFormula: RiskEquationPart[];
  impactFormula: RiskEquationPart[];
  probabilityFormula: RiskEquationPart[];

  formulaPartTypes = EquationPartTypes;
  riskFormulaTypes = RiskFormulaType;

  riskFactors: Array<RiskFactor>;
  searchResults: Array<string>;

  constructor(private dialogService: DialogService,
              private translateService: TranslateService,
              private formulaHelper: AgilityRiskFormulaHelperService,
              private agilityRiskService: AgilityRiskService,
              private spinner: AgilityRiskSpinnerService) {
  }

  initComponent(formula) {
    if (formula) {
      this.startSpinner();
      this.agilityRiskService.getRiskFormulaFactors(formula.id).subscribe({
        next: res => {
          this.riskFactors = res;
          this.formulaUI = formula;
          this.setFactorsLabels(this.formulaUI);
          this.riskFormula = this.formulaUI.equation;
          this.impactFormula = this.formulaUI.formulas?.find(f => f.formulaType === RiskFormulaType.impact)?.equation;
          this.probabilityFormula = this.formulaUI.formulas?.find(f => f.formulaType === RiskFormulaType.probability)?.equation;

          this.spinner.stop();
        },
        error: (error: HttpErrorResponse) => {
          this.spinner.stop();
        }
      });
    }
  }

  setFactorsLabels(formula: RiskFormula) {
    formula.equation?.forEach(ep => {
      if (ep.type !== EquationPartTypes.factor) {
        return;
      }

      ep.label = this.riskFactors.find(rff => rff.id === ep.factorId)?.name;
      if (!ep.label) {
        ep.label = this.translateService.instant('formulaManagement.' + (ep.factorId === RiskFormulaType.impact.toString() ? RiskFormulaType.impact.toString() : RiskFormulaType.probability.toString()) + 'FORMULA');
      }
    });

    if (formula.formulaType === RiskFormulaType.multiPart) {
      formula.formulas?.forEach(f => this.setFactorsLabels(f));
    }
  }

  onSimplify(): void {
  }

  onEditFactors(): void {
    const subscription = this.subscribeToReloadFactors();
    const dlgRef = this.dialogService.open(
      RiskFactorsModalComponent,
      {
        data: {
          formulaId: this.formulaUI.id
        },
        header: this.translateService.instant('formulaManagement.editFactorsModal.header'),
        width: '50%'
      });

    dlgRef.onClose.subscribe(res => {
      if (res) {
        subscription.unsubscribe();
      }
    });

    dlgRef.onDestroy.subscribe(res => {
      subscription.unsubscribe();
    });
  }

  onAddFactor(): void {
    const subscription = this.subscribeToReloadFactors();
    const dlgRef = this.dialogService.open(
      RiskFactorModalComponent,
      {
        data: { formulaId: this.formulaUI.id },
        header: this.translateService.instant('formulaManagement.editFactorModal.header'),
        width: '50%'
      });

    dlgRef.onClose.subscribe((res) => {
      if (res) {
        subscription.unsubscribe();
      }
    });

    dlgRef.onDestroy.subscribe((res) => {
      subscription.unsubscribe();
    });
  }

  loadRiskFactors() {
    this.startSpinner();
    this.agilityRiskService.getRiskFormulaFactors(this.formulaUI.id).subscribe({
      next: factorsRes => {
        this.riskFactors = factorsRes;
        this.setFactorsLabels(this.formulaUI);
        this.spinner.stop();
        this.onFormulaChangedAndValid();
      },
      error: (error: HttpErrorResponse) => {
        this.spinner.stop();
      }
    });
  }

  onTestFormula(): void {
    let riskBand: RiskBand[];
    this.startSpinner();
    this.agilityRiskService.getRiskBandRecalculation(this.formulaUI).subscribe({
      next: (riskBands: Array<RiskBand>) => {
        riskBand = riskBands.sort((a, b) => a.numericValue - b.numericValue);
        this.spinner.stop();
        this.testFormula(riskBand);
      },
      error: (err: HttpErrorResponse) => {
        this.spinner.stop();
      }
    });
  }

  testFormula(riskBands: RiskBand[]) {
    const dlgRef = this.dialogService.open(
      TestFormulaModalComponent,
      {
        data: {
          formula: this.formulaUI,
          riskFactors: this.riskFactors,
          riskBands
        },
        header: this.translateService.instant('formulaManagement.testFormulaModal.header'),
        width: '50%'
      });
  }

  subscribeToReloadFactors(): Subscription {
    return this.formulaHelper.subscribeToRefreshFactor(this.loadRiskFactors.bind(this));
  }

  startSpinner(message?: string) {
    this.spinner.start(message ? message : this.translateService.instant('common.loading'));
  }

  onFormulaChangedAndValid() {
    this.formulaHelper.triggerFormulaChangedAndValid(this.formulaUI);
  }
}
